import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { updateMeta, updateLink } from '../utils/seo'
import '../styles/App.css'

function Tutorial() {
  const [isVisible, setIsVisible] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    setIsVisible(true)
    window.scrollTo(0, 0)

    // Per-page SEO
    document.title = 'Tutorial & Command Guide - Stream Familiars | Twitch Overlay Documentation'
    updateMeta('description', 'Complete guide to Stream Familiars chat commands, settings, and customization. Learn !pet, !feed, !dance, !trick, !hug, !bonk and admin commands for your Twitch stream overlay.')
    updateMeta('og:title', 'Tutorial & Command Guide - Stream Familiars', 'property')
    updateMeta('og:description', 'Complete guide to all Stream Familiars chat commands, settings, and custom critter setup for your Twitch stream overlay.', 'property')
    updateMeta('og:url', 'https://streamfamiliars.com/tutorial', 'property')
    updateLink('canonical', 'https://streamfamiliars.com/tutorial')

    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="tutorial-page">
      {/* Navigation */}
      <nav className={`tut-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="tut-nav-content">
          <Link to="/" className="tut-nav-logo">
            <div className="tut-logo-mark">SF</div>
            <span className="tut-logo-text">Stream Familiars</span>
          </Link>
          <div className="tut-nav-links">
            <a href="#commands">Commands</a>
            <a href="#admin">Admin</a>
            <a href="#2d-critters">2D Critters</a>
            <a href="#3d-models">3D Models</a>
            <a href="#settings">Settings</a>
          </div>
          <div className="tut-nav-right-links">
            <Link to="/guide" className="tut-nav-back">
              Full Docs
            </Link>
            <Link to="/" className="tut-nav-back">
              Home
            </Link>
          </div>
        </div>
      </nav>

      <main className="tut-main">
        {/* Hero */}
        <section className={`tut-hero ${isVisible ? 'visible' : ''}`}>
          <div className="tut-hero-content">
            <span className="tut-section-label">Documentation</span>
            <h1 className="tut-hero-title">
              Tutorial &<br />
              <span className="tut-title-accent">Command Guide</span>
            </h1>
            <p className="tut-hero-desc">
              Everything you need to know about setting up and using Stream Familiars on your channel.
            </p>
          </div>
        </section>

        {/* Getting Started */}
        <section className="tut-section">
          <div className="tut-section-inner">
            <div className="tut-section-header">
              <span className="tut-section-label">Getting Started</span>
              <h2>Quick Start Guide</h2>
              <p>Get your critters live on stream in three simple steps</p>
            </div>

            <div className="tut-steps">
              <div className="tut-step">
                <div className="tut-step-num">1</div>
                <div className="tut-step-body">
                  <h3>Connect Your Twitch Account</h3>
                  <p>Click "Connect Twitch" on the homepage to authenticate with your broadcaster account. This gives Stream Familiars permission to read your chat.</p>
                </div>
              </div>
              <div className="tut-step">
                <div className="tut-step-num">2</div>
                <div className="tut-step-body">
                  <h3>Add as OBS Browser Source</h3>
                  <p>Copy your overlay URL (<code>yourdomain.com/overlay/your_username</code>) and add it as a Browser Source in OBS. Set the width and height to match your canvas (e.g. 1920x1080).</p>
                </div>
              </div>
              <div className="tut-step">
                <div className="tut-step-num">3</div>
                <div className="tut-step-body">
                  <h3>Start Streaming</h3>
                  <p>Subscribers can type <code>!joincritter</code> in chat to spawn their familiar. Your overlay has two settings buttons: <strong>2D Settings</strong> and <strong>3D Settings</strong> to customize each mode independently.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Viewer Commands */}
        <section id="commands" className="tut-section alt">
          <div className="tut-section-inner">
            <div className="tut-section-header">
              <span className="tut-section-label">Chat Commands</span>
              <h2>Viewer Interactions</h2>
              <p>Commands your subscribers can use to interact with their critters</p>
            </div>

            <div className="tut-commands-grid">
              <div className="tut-cmd-card">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!joincritter</code>
                  <span className="tut-cmd-args">[type]</span>
                </div>
                <p>Spawn your critter on the stream overlay. Optionally specify a type like <code>cat</code>, <code>bunny</code>, <code>owl</code>, <code>fox</code>, <code>penguin</code>, <code>hamster</code>, or <code>slime</code>.</p>
                <div className="tut-cmd-example">
                  <span className="tut-cmd-label">Example</span>
                  <code>!joincritter fox</code>
                </div>
              </div>

              <div className="tut-cmd-card">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!leavecritter</code>
                </div>
                <p>Remove your critter from the overlay. You can always rejoin later with <code>!joincritter</code>.</p>
              </div>

              <div className="tut-cmd-card">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!crittertype</code>
                  <span className="tut-cmd-args">&lt;type&gt;</span>
                </div>
                <p>Change your critter to a different type without leaving and rejoining.</p>
                <div className="tut-cmd-example">
                  <span className="tut-cmd-label">Example</span>
                  <code>!crittertype penguin</code>
                </div>
              </div>

              <div className="tut-cmd-card highlight">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!pet</code>
                  <span className="tut-cmd-effect">Hearts</span>
                </div>
                <p>Pet your critter! Shows a cute bounce animation with floating hearts above your familiar.</p>
              </div>

              <div className="tut-cmd-card highlight">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!feed</code>
                  <span className="tut-cmd-effect">Food</span>
                </div>
                <p>Feed your critter! Triggers an eating animation with food particles floating above.</p>
              </div>

              <div className="tut-cmd-card highlight">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!dance</code>
                  <span className="tut-cmd-effect">Music</span>
                </div>
                <p>Make your critter dance! It sways side to side with floating music notes for 3 seconds.</p>
              </div>

              <div className="tut-cmd-card highlight">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!trick</code>
                  <span className="tut-cmd-effect">Sparkles</span>
                </div>
                <p>Your critter does a flip! Shows a spinning animation with sparkle effects around it.</p>
              </div>

              <div className="tut-cmd-card social">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!hug</code>
                  <span className="tut-cmd-args">@username</span>
                  <span className="tut-cmd-effect">Hearts</span>
                </div>
                <p>Hug another viewer's critter! Your critter walks over to theirs and both show a warm squish animation with hearts.</p>
                <div className="tut-cmd-example">
                  <span className="tut-cmd-label">Example</span>
                  <code>!hug @friendname</code>
                </div>
              </div>

              <div className="tut-cmd-card social">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!bonk</code>
                  <span className="tut-cmd-args">@username</span>
                  <span className="tut-cmd-effect">Stars</span>
                </div>
                <p>Playfully bonk another viewer's critter! The target critter shakes with star burst effects.</p>
                <div className="tut-cmd-example">
                  <span className="tut-cmd-label">Example</span>
                  <code>!bonk @rivalname</code>
                </div>
              </div>
            </div>

            <div className="tut-info-box">
              <div className="tut-info-icon">💬</div>
              <div>
                <strong>Chat Bubbles</strong>
                <p>Any regular chat message (not starting with !) will appear as a speech bubble above your critter. Messages are truncated to 140 characters.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Admin Commands */}
        <section id="admin" className="tut-section">
          <div className="tut-section-inner">
            <div className="tut-section-header">
              <span className="tut-section-label">Admin Commands</span>
              <h2>Broadcaster & Mod Controls</h2>
              <p>Powerful commands for managing critters on your stream</p>
            </div>

            <div className="tut-commands-grid">
              <div className="tut-cmd-card admin">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!feast</code>
                  <span className="tut-cmd-badge">Admin</span>
                </div>
                <p>Feed ALL critters at once! Makes it rain food particles on every critter on screen. A great way to celebrate a hype moment.</p>
              </div>

              <div className="tut-cmd-card admin">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!addcritter</code>
                  <span className="tut-cmd-args">@user [type]</span>
                  <span className="tut-cmd-badge">Admin</span>
                </div>
                <p>Manually add a critter for a specific viewer, even if they haven't joined themselves.</p>
                <div className="tut-cmd-example">
                  <span className="tut-cmd-label">Example</span>
                  <code>!addcritter @newviewer cat</code>
                </div>
              </div>

              <div className="tut-cmd-card admin">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!removecritter</code>
                  <span className="tut-cmd-args">@user</span>
                  <span className="tut-cmd-badge">Admin</span>
                </div>
                <p>Remove a specific viewer's critter from the overlay.</p>
              </div>

              <div className="tut-cmd-card admin">
                <div className="tut-cmd-header">
                  <code className="tut-cmd">!clearcritters</code>
                  <span className="tut-cmd-badge">Admin</span>
                </div>
                <p>Remove ALL critters from the overlay at once. Use with caution!</p>
              </div>
            </div>

            <div className="tut-info-box warning">
              <div className="tut-info-icon">🔒</div>
              <div>
                <strong>Permissions</strong>
                <p>Admin commands require mod or broadcaster status by default. You can configure this in Settings &gt; Permissions to be broadcaster-only or allow mods.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 2D Critters */}
        <section id="2d-critters" className="tut-section alt">
          <div className="tut-section-inner">
            <div className="tut-section-header">
              <span className="tut-section-label">2D Sprites</span>
              <h2>2D Animated Critters</h2>
              <p>Static images, CSS animations, and spritesheet support for classic pixel-art style overlays</p>
            </div>

            <div className="tut-custom-grid">
              <div className="tut-custom-card">
                <h3>Default 2D Types</h3>
                <p>8 built-in 2D critter types with static sprite images:</p>
                <div className="tut-type-tags">
                  <span className="tut-tag tut-tag-2d">Cat</span>
                  <span className="tut-tag tut-tag-2d">Bunny</span>
                  <span className="tut-tag tut-tag-2d">Owl</span>
                  <span className="tut-tag tut-tag-2d">Slime</span>
                  <span className="tut-tag tut-tag-2d">Fox</span>
                  <span className="tut-tag tut-tag-2d">Penguin</span>
                  <span className="tut-tag tut-tag-2d">Hamster</span>
                  <span className="tut-tag tut-tag-2d">Panda</span>
                </div>
                <div className="tut-note" style={{marginTop: '16px'}}>
                  Create unlimited custom 2D types by uploading your own PNG images via 2D Settings &gt; Admin &gt; "Create Custom Critter".
                </div>
              </div>

              <div className="tut-custom-card">
                <h3>How 2D Critters Work</h3>
                <p>Each 2D critter uses two PNG images that swap to create animations.</p>
                <ol>
                  <li><strong>Mouth Closed</strong> - default idle state</li>
                  <li><strong>Mouth Open</strong> - shown when eating, talking</li>
                  <li>CSS handles breathing, walking sway, and effect animations</li>
                  <li>Optional <strong>Smiling</strong> image for happy states</li>
                </ol>
              </div>

              <div className="tut-custom-card">
                <h3>Creating Custom 2D Types</h3>
                <p>Add your own artwork as new critter types.</p>
                <ol>
                  <li>Click <strong style={{color: '#a78bfa'}}>2D Settings</strong> on your overlay</li>
                  <li>Go to the <strong>Admin</strong> tab</li>
                  <li>Click "Create Custom Critter"</li>
                  <li>Upload mouth-closed and mouth-open PNG images</li>
                  <li>Give it a name and type key</li>
                  <li>Viewers can now use <code>!joincritter yourtype</code></li>
                </ol>
                <div className="tut-note">
                  Use transparent PNGs, 128x128 to 256x256px recommended. Max 5MB per image.
                </div>
              </div>
            </div>

            <div className="tut-info-box" style={{marginTop: '32px'}}>
              <div className="tut-info-icon">🎞️</div>
              <div>
                <strong>Animated Spritesheets</strong>
                <p>
                  For frame-by-frame animations, upload spritesheet PNG + JSON metadata files via Admin &gt; "Upload Spritesheets".
                  Supports 4 animation states: <strong>Idle</strong>, <strong>Walk</strong>, <strong>Eat</strong>, and <strong>Pet</strong>.
                  Enable in 2D Settings &gt; Visual &gt; "Enable Animated Sprites". Use TexturePacker JSON (Array) format.
                  Animation FPS is configurable (6-30 fps, default 12).
                </p>
              </div>
            </div>

            <div className="tut-info-box">
              <div className="tut-info-icon">✨</div>
              <div>
                <strong>2D Interaction Effects</strong>
                <p>
                  <strong>!pet</strong> - bounce + floating hearts &nbsp;|&nbsp;
                  <strong>!feed</strong> - eating + food particles &nbsp;|&nbsp;
                  <strong>!dance</strong> - sway + music notes &nbsp;|&nbsp;
                  <strong>!trick</strong> - flip + sparkles &nbsp;|&nbsp;
                  <strong>!hug</strong> - walk to target + squish + hearts &nbsp;|&nbsp;
                  <strong>!bonk</strong> - shake + star burst &nbsp;|&nbsp;
                  <strong>Sleep</strong> - greyed out + Zzz
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 3D Models */}
        <section id="3d-models" className="tut-section">
          <div className="tut-section-inner">
            <div className="tut-section-header">
              <span className="tut-section-label">3D Models</span>
              <h2>3D Animated Critters</h2>
              <p>Upload GLB models with full animation support for immersive stream overlays</p>
            </div>

            <div className="tut-custom-grid">
              <div className="tut-custom-card">
                <h3>Available 3D Types</h3>
                <p>27 built-in 3D critter types ready for model uploads:</p>
                <div className="tut-type-tags">
                  <span className="tut-tag farm">Buffalo</span>
                  <span className="tut-tag farm">Chick</span>
                  <span className="tut-tag farm">Cow</span>
                  <span className="tut-tag farm">Donkey</span>
                  <span className="tut-tag farm">Duck</span>
                  <span className="tut-tag farm">Hen</span>
                  <span className="tut-tag farm">Pig</span>
                  <span className="tut-tag farm">Rooster</span>
                  <span className="tut-tag farm">Sheep</span>
                  <span className="tut-tag wild">Crow</span>
                  <span className="tut-tag wild">Eagle</span>
                  <span className="tut-tag wild">Fox</span>
                  <span className="tut-tag wild">Hog</span>
                  <span className="tut-tag wild">Hornbill</span>
                  <span className="tut-tag wild">Owl</span>
                  <span className="tut-tag wild">Raccoon</span>
                  <span className="tut-tag wild">Snake</span>
                  <span className="tut-tag wild">Wolf</span>
                  <span className="tut-tag domestic">Cat</span>
                  <span className="tut-tag domestic">Dog</span>
                  <span className="tut-tag domestic">Dove</span>
                  <span className="tut-tag domestic">Goldfish</span>
                  <span className="tut-tag domestic">Mice</span>
                  <span className="tut-tag domestic">Parrot</span>
                  <span className="tut-tag domestic">Pigeon</span>
                  <span className="tut-tag domestic">Rabbit</span>
                  <span className="tut-tag domestic">Tortoise</span>
                </div>
              </div>

              <div className="tut-custom-card">
                <h3>How to Add a 3D Model</h3>
                <p>Upload GLB/GLTF files and map animations to critter behaviors.</p>
                <ol>
                  <li>Click <strong>3D Settings</strong> on your overlay</li>
                  <li>Go to the <strong>Upload Models</strong> tab</li>
                  <li>Select a critter type and upload a GLB file</li>
                  <li>Animations are auto-detected and mapped by name</li>
                  <li>Adjust the model scale as needed</li>
                  <li>Click Upload - the type switches to 3D rendering automatically</li>
                </ol>
                <div className="tut-note">
                  Models should be GLB format, under 10MB, with less than 10K triangles for smooth overlay performance.
                </div>
              </div>
            </div>

            <div className="tut-info-box" style={{marginTop: '32px'}}>
              <div className="tut-info-icon">🎬</div>
              <div>
                <strong>Supported Animations (14 States)</strong>
                <p>
                  <strong>Idle A/B/C</strong> - cycle every 5s &nbsp;|&nbsp;
                  <strong>Walk / Run</strong> - roaming &nbsp;|&nbsp;
                  <strong>Eat</strong> - !feed / !feast &nbsp;|&nbsp;
                  <strong>Bounce</strong> - !pet &nbsp;|&nbsp;
                  <strong>Spin</strong> - !dance &nbsp;|&nbsp;
                  <strong>Roll</strong> - !trick &nbsp;|&nbsp;
                  <strong>Sit</strong> - !hug &nbsp;|&nbsp;
                  <strong>Hit</strong> - !bonk &nbsp;|&nbsp;
                  <strong>Sleep / Spawn / Despawn</strong> - lifecycle
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Settings & Features */}
        <section id="settings" className="tut-section alt">
          <div className="tut-section-inner">
            <div className="tut-section-header">
              <span className="tut-section-label">Configuration</span>
              <h2>Settings & Features</h2>
              <p>Two separate settings panels keep 2D and 3D configuration clean and organized</p>
            </div>

            <div className="tut-info-box" style={{marginBottom: '32px'}}>
              <div className="tut-info-icon">🖥️</div>
              <div>
                <strong>Split Settings Panels</strong>
                <p>Your overlay has two buttons: <strong style={{color: '#a78bfa'}}>2D Settings</strong> (purple) for sprite-based critters and <strong style={{color: '#10b981'}}>3D Settings</strong> (green) for 3D model critters. Each panel only shows critter types relevant to its mode.</p>
              </div>
            </div>

            <div className="tut-features-grid">
              <div className="tut-feature-card" style={{borderTop: '3px solid #a78bfa'}}>
                <div className="tut-feature-icon">🎨</div>
                <h3>2D Settings</h3>
                <ul>
                  <li><strong>Movement</strong> - Static mode, roam interval, speed, boundaries</li>
                  <li><strong>Visual</strong> - Scale, usernames, chat bubbles, animated sprites, FPS</li>
                  <li><strong>Features</strong> - Toggle commands, sleep mode, critter limits</li>
                  <li><strong>Permissions</strong> - Subscription tiers, admin access</li>
                  <li><strong>Admin</strong> - Add/remove 2D critters, custom types, spritesheets</li>
                </ul>
              </div>

              <div className="tut-feature-card" style={{borderTop: '3px solid #10b981'}}>
                <div className="tut-feature-icon">🧊</div>
                <h3>3D Settings</h3>
                <ul>
                  <li><strong>Enable 3D</strong> - Global toggle for 3D model rendering</li>
                  <li><strong>Global 3D Scale</strong> - Scale multiplier for all 3D models</li>
                  <li><strong>Upload Models</strong> - Upload GLB/GLTF with animation mapping</li>
                  <li><strong>Model Library</strong> - View and manage uploaded 3D models</li>
                  <li><strong>Admin</strong> - Add/remove 3D critters, active 3D critter list</li>
                </ul>
              </div>

              <div className="tut-feature-card">
                <div className="tut-feature-icon">🎮</div>
                <h3>Movement</h3>
                <ul>
                  <li><strong>Static Mode</strong> - Critters stay in place, no roaming</li>
                  <li><strong>Roam Interval</strong> - How often critters pick new destinations</li>
                  <li><strong>Movement Speed</strong> - How fast they walk across the screen</li>
                  <li><strong>Boundaries</strong> - Define the area critters can roam (X/Y min/max)</li>
                </ul>
              </div>

              <div className="tut-feature-card">
                <div className="tut-feature-icon">⚡</div>
                <h3>Feature Toggles</h3>
                <ul>
                  <li>Enable/disable each command individually (!pet, !feed, !dance, !trick, !hug, !bonk)</li>
                  <li><strong>Chat Bubbles</strong> - Toggle speech bubbles on/off</li>
                  <li><strong>Critter Type Change</strong> - Allow viewers to switch types</li>
                </ul>
              </div>

              <div className="tut-feature-card">
                <div className="tut-feature-icon">😴</div>
                <h3>Sleep Mode</h3>
                <ul>
                  <li>Inactive viewers' critters automatically fall asleep</li>
                  <li>Sleeping critters become greyed out with floating Zzz</li>
                  <li>They stop moving until the viewer chats again</li>
                  <li><strong>Sleep Timeout</strong> - Configurable from 1 to 30 minutes</li>
                </ul>
              </div>

              <div className="tut-feature-card">
                <div className="tut-feature-icon">🔐</div>
                <h3>Permissions</h3>
                <ul>
                  <li><strong>Subscription Requirement</strong> - Require Tier 1, 2, or 3 subs</li>
                  <li><strong>Admin Access</strong> - Broadcaster-only or allow mods</li>
                  <li><strong>Max Critters</strong> - Limit active critters (5-50)</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Customization */}
        <section id="customization" className="tut-section">
          <div className="tut-section-inner">
            <div className="tut-section-header">
              <span className="tut-section-label">Customization</span>
              <h2>Custom Critters & Assets</h2>
              <p>Make your stream overlay truly unique with custom 2D sprites or 3D models</p>
            </div>

            <div className="tut-custom-grid">
              <div className="tut-custom-card">
                <h3>Custom 2D Critter Types</h3>
                <p>Create entirely new critter types with your own artwork.</p>
                <ol>
                  <li>Open <strong>2D Settings</strong> &gt; Admin tab</li>
                  <li>Click "Create Custom Critter"</li>
                  <li>Upload two PNG images: mouth open and mouth closed</li>
                  <li>Give it a name and key (e.g., "dragon")</li>
                  <li>Viewers can now use <code>!joincritter dragon</code></li>
                </ol>
              </div>

              <div className="tut-custom-card">
                <h3>Animated Spritesheets</h3>
                <p>For advanced 2D animations with multiple frames per state.</p>
                <ol>
                  <li>Enable "Animated Sprites" in <strong>2D Settings</strong> &gt; Visual</li>
                  <li>Open Admin &gt; "Upload Spritesheets"</li>
                  <li>Upload a PNG spritesheet + JSON metadata for each animation state (idle, walk, eat, pet)</li>
                  <li>The JSON defines frame positions, sizes, and order</li>
                </ol>
                <div className="tut-note">
                  Spritesheets override static images when enabled. If a specific animation state is missing, it falls back to idle.
                </div>
              </div>

              <div className="tut-custom-card">
                <h3>3D Model Upload</h3>
                <p>Upload GLB/GLTF 3D models for any 3D critter type.</p>
                <ol>
                  <li>Open <strong>3D Settings</strong> &gt; Upload Models tab</li>
                  <li>Select a 3D critter type from the dropdown</li>
                  <li>Upload your GLB file - animations are auto-detected</li>
                  <li>Map animation clips to behaviors (idle, walk, eat, dance, etc.)</li>
                  <li>Set the model scale and click Upload</li>
                </ol>
                <div className="tut-note">
                  Once uploaded, the critter type automatically switches to 3D rendering. Models support 14 animation states with fallback chains.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Command Quick Reference */}
        <section className="tut-section alt">
          <div className="tut-section-inner">
            <div className="tut-section-header">
              <span className="tut-section-label">Reference</span>
              <h2>Quick Command Reference</h2>
              <p>All commands at a glance</p>
            </div>

            <div className="tut-ref-table">
              <div className="tut-ref-row header">
                <span>Command</span>
                <span>Description</span>
                <span>Access</span>
              </div>
              <div className="tut-ref-row">
                <code>!joincritter [type]</code>
                <span>Spawn your critter</span>
                <span className="tut-access sub">Subscriber</span>
              </div>
              <div className="tut-ref-row">
                <code>!leavecritter</code>
                <span>Remove your critter</span>
                <span className="tut-access sub">Subscriber</span>
              </div>
              <div className="tut-ref-row">
                <code>!crittertype &lt;type&gt;</code>
                <span>Change critter type</span>
                <span className="tut-access sub">Subscriber</span>
              </div>
              <div className="tut-ref-row">
                <code>!pet</code>
                <span>Pet your critter (hearts)</span>
                <span className="tut-access sub">Subscriber</span>
              </div>
              <div className="tut-ref-row">
                <code>!feed</code>
                <span>Feed your critter (food particles)</span>
                <span className="tut-access sub">Subscriber</span>
              </div>
              <div className="tut-ref-row">
                <code>!dance</code>
                <span>Dance with music notes</span>
                <span className="tut-access sub">Subscriber</span>
              </div>
              <div className="tut-ref-row">
                <code>!trick</code>
                <span>Flip with sparkle effects</span>
                <span className="tut-access sub">Subscriber</span>
              </div>
              <div className="tut-ref-row">
                <code>!hug @user</code>
                <span>Hug another viewer's critter</span>
                <span className="tut-access sub">Subscriber</span>
              </div>
              <div className="tut-ref-row">
                <code>!bonk @user</code>
                <span>Bonk another viewer's critter</span>
                <span className="tut-access sub">Subscriber</span>
              </div>
              <div className="tut-ref-row">
                <code>!feast</code>
                <span>Feed ALL critters at once</span>
                <span className="tut-access admin">Admin</span>
              </div>
              <div className="tut-ref-row">
                <code>!addcritter @user [type]</code>
                <span>Add critter for a user</span>
                <span className="tut-access admin">Admin</span>
              </div>
              <div className="tut-ref-row">
                <code>!removecritter @user</code>
                <span>Remove user's critter</span>
                <span className="tut-access admin">Admin</span>
              </div>
              <div className="tut-ref-row">
                <code>!clearcritters</code>
                <span>Remove all critters</span>
                <span className="tut-access admin">Admin</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="tut-cta">
          <div className="tut-cta-inner">
            <h2>Ready to get started?</h2>
            <p>Set up your stream overlay in under a minute</p>
            <div className="tut-cta-actions">
              <Link to="/" className="tut-btn-primary">
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="tut-footer">
        <div className="tut-footer-content">
          <div className="tut-footer-brand">
            <div className="tut-logo-mark small">SF</div>
            <span>Stream Familiars</span>
          </div>
          <div className="tut-footer-meta">
            <span>2026</span>
            <span className="tut-dot"></span>
            <span>Made for streamers</span>
          </div>
        </div>
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .tutorial-page {
          min-height: 100vh;
          background: #0a0a0b;
          color: #e4e4e7;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          overflow-x: hidden;
        }

        body { overflow: auto !important; }

        /* Nav */
        .tut-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          padding: 16px 24px;
          transition: all 0.3s ease;
        }

        .tut-nav.scrolled {
          background: rgba(10, 10, 11, 0.8);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .tut-nav-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .tut-nav-logo {
          display: flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
          color: inherit;
        }

        .tut-logo-mark {
          width: 36px;
          height: 36px;
          background: #a78bfa;
          color: #0a0a0b;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
        }

        .tut-logo-mark.small {
          width: 28px;
          height: 28px;
          font-size: 11px;
          border-radius: 8px;
        }

        .tut-logo-text {
          font-weight: 600;
          font-size: 15px;
          color: #fafafa;
        }

        .tut-nav-links {
          display: flex;
          gap: 32px;
        }

        .tut-nav-links a {
          color: #71717a;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: color 0.2s;
        }

        .tut-nav-links a:hover {
          color: #fafafa;
        }

        .tut-nav-back {
          background: rgba(255,255,255,0.08);
          color: #fafafa;
          border: 1px solid rgba(255,255,255,0.1);
          padding: 10px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.2s;
        }

        .tut-nav-back:hover {
          background: rgba(255,255,255,0.12);
          border-color: rgba(255,255,255,0.15);
        }

        .tut-nav-right-links {
          display: flex;
          gap: 8px;
        }

        /* Hero */
        .tut-main {
          padding-top: 100px;
        }

        .tut-hero {
          max-width: 1200px;
          margin: 0 auto;
          padding: 60px 24px 40px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .tut-hero.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .tut-hero-content {
          text-align: center;
        }

        .tut-hero-title {
          font-size: clamp(36px, 5vw, 56px);
          font-weight: 800;
          line-height: 1.1;
          letter-spacing: -0.03em;
          color: #fafafa;
          margin-bottom: 20px;
        }

        .tut-title-accent {
          color: #a78bfa;
        }

        .tut-hero-desc {
          font-size: 18px;
          line-height: 1.7;
          color: #71717a;
          max-width: 560px;
          margin: 0 auto;
        }

        /* Sections */
        .tut-section {
          padding: 80px 24px;
        }

        .tut-section.alt {
          background: #0c0c0d;
        }

        .tut-section-inner {
          max-width: 1200px;
          margin: 0 auto;
        }

        .tut-section-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .tut-section-label {
          display: inline-block;
          font-size: 13px;
          font-weight: 600;
          color: #a78bfa;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 16px;
        }

        .tut-section-header h2 {
          font-size: clamp(28px, 4vw, 42px);
          font-weight: 700;
          color: #fafafa;
          margin-bottom: 12px;
          letter-spacing: -0.02em;
        }

        .tut-section-header p {
          font-size: 17px;
          color: #71717a;
        }

        /* Steps */
        .tut-steps {
          max-width: 700px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .tut-step {
          display: flex;
          gap: 20px;
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 24px;
          transition: all 0.3s;
        }

        .tut-step:hover {
          border-color: rgba(167, 139, 250, 0.2);
        }

        .tut-step-num {
          width: 40px;
          height: 40px;
          background: rgba(167, 139, 250, 0.1);
          color: #a78bfa;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 16px;
          flex-shrink: 0;
        }

        .tut-step-body h3 {
          font-size: 17px;
          font-weight: 600;
          color: #fafafa;
          margin-bottom: 6px;
        }

        .tut-step-body p {
          font-size: 14px;
          color: #71717a;
          line-height: 1.6;
        }

        .tut-step-body code {
          background: rgba(167, 139, 250, 0.1);
          color: #c4b5fd;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 13px;
        }

        /* Command Cards */
        .tut-commands-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 16px;
        }

        .tut-cmd-card {
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 24px;
          transition: all 0.3s;
        }

        .tut-cmd-card:hover {
          border-color: rgba(255,255,255,0.12);
          transform: translateY(-2px);
        }

        .tut-cmd-card.highlight {
          border-left: 3px solid #a78bfa;
        }

        .tut-cmd-card.social {
          border-left: 3px solid #3b82f6;
        }

        .tut-cmd-card.admin {
          border-left: 3px solid #f97316;
        }

        .tut-cmd-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }

        .tut-cmd {
          background: rgba(167, 139, 250, 0.15);
          color: #c4b5fd;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
        }

        .tut-cmd-args {
          color: #71717a;
          font-size: 14px;
        }

        .tut-cmd-effect {
          background: rgba(167, 139, 250, 0.08);
          color: #a78bfa;
          padding: 3px 10px;
          border-radius: 100px;
          font-size: 12px;
          font-weight: 500;
        }

        .tut-cmd-badge {
          background: rgba(249, 115, 22, 0.1);
          color: #f97316;
          padding: 3px 10px;
          border-radius: 100px;
          font-size: 12px;
          font-weight: 600;
        }

        .tut-cmd-card p {
          font-size: 14px;
          color: #a1a1aa;
          line-height: 1.6;
          margin-bottom: 0;
        }

        .tut-cmd-card p code {
          background: rgba(255,255,255,0.05);
          color: #d4d4d8;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 13px;
        }

        .tut-cmd-example {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid rgba(255,255,255,0.04);
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .tut-cmd-label {
          font-size: 11px;
          font-weight: 600;
          color: #52525b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .tut-cmd-example code {
          color: #71717a;
          font-size: 13px;
        }

        /* Info boxes */
        .tut-info-box {
          display: flex;
          gap: 16px;
          background: rgba(167, 139, 250, 0.05);
          border: 1px solid rgba(167, 139, 250, 0.1);
          border-radius: 14px;
          padding: 20px 24px;
          margin-top: 32px;
        }

        .tut-info-box.warning {
          background: rgba(249, 115, 22, 0.05);
          border-color: rgba(249, 115, 22, 0.1);
        }

        .tut-info-icon {
          font-size: 24px;
          flex-shrink: 0;
        }

        .tut-info-box strong {
          color: #fafafa;
          font-size: 15px;
          display: block;
          margin-bottom: 4px;
        }

        .tut-info-box p {
          color: #71717a;
          font-size: 14px;
          line-height: 1.6;
          margin: 0;
        }

        /* Feature Cards */
        .tut-features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 20px;
        }

        .tut-feature-card {
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 28px;
          transition: all 0.3s;
        }

        .tut-feature-card:hover {
          border-color: rgba(255,255,255,0.12);
        }

        .tut-feature-icon {
          font-size: 28px;
          margin-bottom: 16px;
        }

        .tut-feature-card h3 {
          font-size: 18px;
          font-weight: 600;
          color: #fafafa;
          margin-bottom: 16px;
        }

        .tut-feature-card ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .tut-feature-card li {
          font-size: 14px;
          color: #a1a1aa;
          line-height: 1.5;
          padding-left: 16px;
          position: relative;
        }

        .tut-feature-card li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 8px;
          width: 5px;
          height: 5px;
          background: #a78bfa;
          border-radius: 50%;
        }

        .tut-feature-card li strong {
          color: #d4d4d8;
        }

        /* Customization */
        .tut-custom-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 24px;
        }

        .tut-custom-card {
          background: #141416;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 32px;
        }

        .tut-custom-card h3 {
          font-size: 20px;
          font-weight: 600;
          color: #fafafa;
          margin-bottom: 12px;
        }

        .tut-custom-card > p {
          font-size: 15px;
          color: #71717a;
          line-height: 1.6;
          margin-bottom: 20px;
        }

        .tut-custom-card ol {
          padding-left: 20px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .tut-custom-card li {
          font-size: 14px;
          color: #a1a1aa;
          line-height: 1.5;
        }

        .tut-custom-card li code {
          background: rgba(167, 139, 250, 0.1);
          color: #c4b5fd;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 13px;
        }

        .tut-note {
          margin-top: 16px;
          padding: 12px 16px;
          background: rgba(255,255,255,0.03);
          border-left: 3px solid #a78bfa;
          border-radius: 0 8px 8px 0;
          font-size: 13px;
          color: #71717a;
          line-height: 1.5;
        }

        /* Reference Table */
        .tut-ref-table {
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          overflow: hidden;
        }

        .tut-ref-row {
          display: grid;
          grid-template-columns: 1fr 1.2fr auto;
          gap: 20px;
          padding: 16px 24px;
          align-items: center;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        .tut-ref-row:last-child {
          border-bottom: none;
        }

        .tut-ref-row.header {
          background: rgba(255,255,255,0.03);
          font-size: 12px;
          font-weight: 600;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .tut-ref-row code {
          background: rgba(167, 139, 250, 0.1);
          color: #c4b5fd;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
        }

        .tut-ref-row span {
          font-size: 14px;
          color: #a1a1aa;
        }

        .tut-access {
          padding: 4px 12px;
          border-radius: 100px;
          font-size: 12px;
          font-weight: 600;
        }

        .tut-access.sub {
          background: rgba(167, 139, 250, 0.1);
          color: #a78bfa;
        }

        .tut-access.admin {
          background: rgba(249, 115, 22, 0.1);
          color: #f97316;
        }

        /* CTA */
        .tut-cta {
          padding: 80px 24px;
          text-align: center;
        }

        .tut-cta-inner {
          max-width: 600px;
          margin: 0 auto;
        }

        .tut-cta h2 {
          font-size: clamp(24px, 3vw, 36px);
          font-weight: 700;
          color: #fafafa;
          margin-bottom: 12px;
        }

        .tut-cta p {
          font-size: 16px;
          color: #71717a;
          margin-bottom: 32px;
        }

        .tut-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: #a78bfa;
          color: #0a0a0b;
          border: none;
          padding: 14px 28px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.2s;
        }

        .tut-btn-primary:hover {
          background: #c4b5fd;
          transform: translateY(-2px);
        }

        /* Footer */
        .tut-footer {
          border-top: 1px solid rgba(255,255,255,0.06);
          padding: 24px;
        }

        .tut-footer-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .tut-footer-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #71717a;
          font-size: 14px;
        }

        .tut-footer-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          color: #52525b;
          font-size: 13px;
        }

        .tut-dot {
          width: 3px;
          height: 3px;
          background: #3f3f46;
          border-radius: 50%;
        }

        /* Type Tags */
        .tut-type-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 16px;
        }

        .tut-tag {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 100px;
          font-size: 12px;
          font-weight: 600;
        }

        .tut-tag-2d {
          background: rgba(167, 139, 250, 0.1);
          color: #a78bfa;
        }

        .tut-tag.farm {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .tut-tag.wild {
          background: rgba(249, 115, 22, 0.1);
          color: #f97316;
        }

        .tut-tag.domestic {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        /* Mobile */
        @media (max-width: 968px) {
          .tut-commands-grid,
          .tut-features-grid {
            grid-template-columns: 1fr;
          }

          .tut-custom-grid {
            grid-template-columns: 1fr;
          }

          .tut-ref-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .tut-ref-row.header {
            display: none;
          }
        }

        @media (max-width: 640px) {
          .tut-nav {
            padding: 12px 16px;
          }

          .tut-nav-links {
            display: none;
          }

          .tut-hero {
            padding: 40px 16px;
          }

          .tut-section {
            padding: 60px 16px;
          }

          .tut-footer-content {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }
        }
      `}</style>
    </div>
  )
}

export default Tutorial